import { useMutation } from "@apollo/client";
import ChevronRight from "@mui/icons-material/ChevronRight";
import { Button, ButtonProps, Skeleton, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { DocumentDialogContainer } from "../shared";
import {
  DocumentAssociationsInput,
  DocumentType,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { documentTypeToLabel } from "util/constants";
import { DocumentMetadataFormSections } from "./components/DocumentMetadataFormSections";
import { DocumentMetadataFormSectionsSkeleton } from "./components/DocumentMetadataFormSectionsSkeleton";
import { DocumentSwitcherSelect } from "./components/DocumentSwitcherSelect";
import { useDocumentMetadata } from "./hooks/useDocumentMetadata";
import {
  DocumentMetadataFormData,
  ReviewDocumentMetadataDialogProps,
} from "./types";
import { UPSERT_DOCUMENT_WITH_METADATA } from "./queries";

export function ReviewDocumentMetadataDialog({
  open,
  onClose,
  documentIds,
  tenantId,
  onComplete,
  title,
  hasDocumentAI = true,
  onCurrentDocumentChange,
}: ReviewDocumentMetadataDialogProps) {
  const theme = useTheme();
  const alerts = useAlerts();
  const [currentDocumentId, setCurrentDocumentId] = useState<
    string | undefined
  >(() => documentIds[0]);
  const currentIndex = useMemo(() => {
    if (!currentDocumentId) {
      return 0;
    }
    const index = documentIds.indexOf(currentDocumentId);
    return index >= 0 ? index : 0;
  }, [currentDocumentId, documentIds]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const onDocumentChange = useCallback(
    (documentId: string) => {
      if (onCurrentDocumentChange) {
        onCurrentDocumentChange(documentId);
      }
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    [onCurrentDocumentChange]
  );

  useEffect(() => {
    if (documentIds.length === 0) {
      if (currentDocumentId !== undefined) {
        setCurrentDocumentId(undefined);
      }
      return;
    }

    if (!currentDocumentId || !documentIds.includes(currentDocumentId)) {
      const fallbackId = documentIds[0];
      setCurrentDocumentId(fallbackId);
      onDocumentChange(fallbackId);
    }
  }, [documentIds, currentDocumentId, onDocumentChange]);

  const {
    document,
    metadata,
    loading,
    suggestedFacilities,
    suggestedFacilitiesLoading,
    previewUrl,
  } = useDocumentMetadata({
    documentId: currentDocumentId,
    open,
    hasDocumentAI,
  });

  const handleSelectDocument = useCallback(
    (documentId: string) => {
      if (!documentId || documentId === currentDocumentId) {
        return;
      }
      setCurrentDocumentId(documentId);
      onDocumentChange(documentId);
    },
    [currentDocumentId, onDocumentChange]
  );

  const [upsertDocumentWithMetadata, { loading: saving }] = useMutation(
    UPSERT_DOCUMENT_WITH_METADATA
  );

  const methods = useForm<DocumentMetadataFormData>({
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods;

  // Reset form when document changes
  useEffect(() => {
    if (document) {
      reset({
        // Document fields
        documentTitle: document.title,
        documentType: document.documentType ?? DocumentType.Other,
        dateAuthored: document.authoredAt ?? null,
        description: document.description ?? "",
        facilities:
          document.facilities?.map((facility) => ({
            id: facility.id,
            name: facility.name ?? "",
            customerFacilityId: facility.customerFacilityId ?? null,
            streetAddress1: facility.streetAddress1 ?? null,
            streetAddress2: facility.streetAddress2 ?? null,
            city: facility.city ?? null,
            state: facility.state ?? null,
            zip: facility.zip ?? null,
          })) ?? [],
        documentTags:
          document.documentTags?.map((t) => ({
            id: t.id,
            name: t.name,
            type: t.type,
          })) ?? [],

        // Basic Document Classification
        documentSubtype: metadata?.documentSubtype ?? "",
        documentNumber: metadata?.documentNumber ?? "",
        relatedDocumentNumber: metadata?.relatedDocumentNumber ?? "",
        revisionNumber: metadata?.revisionNumber ?? "",
        reportingPeriod: metadata?.reportingPeriod ?? "",

        // Site/Facility Information
        siteName: metadata?.siteName ?? "",
        siteAddressLine1: metadata?.siteAddressLine1 ?? "",
        siteAddressLine2: metadata?.siteAddressLine2 ?? "",
        siteCity: metadata?.siteCity ?? "",
        siteState: metadata?.siteState ?? "",
        siteZipCode: metadata?.siteZipCode ?? "",
        siteEmail: metadata?.siteEmail ?? "",

        // Mailing Address
        mailingName: metadata?.mailingName ?? "",
        mailingAddressLine1: metadata?.mailingAddressLine1 ?? "",
        mailingAddressLine2: metadata?.mailingAddressLine2 ?? "",
        mailingCity: metadata?.mailingCity ?? "",
        mailingState: metadata?.mailingState ?? "",
        mailingZipCode: metadata?.mailingZipCode ?? "",
        mailingEmail: metadata?.mailingEmail ?? "",

        // Contact Information
        contactName: metadata?.contactName ?? "",
        contactEmail: metadata?.contactEmail ?? "",
        contactPhone: metadata?.contactPhone ?? "",
        contactAddressLine1: metadata?.contactAddressLine1 ?? "",
        contactAddressLine2: metadata?.contactAddressLine2 ?? "",
        contactCity: metadata?.contactCity ?? "",
        contactState: metadata?.contactState ?? "",
        contactZipCode: metadata?.contactZipCode ?? "",
        agency: metadata?.agency ?? "",

        // Program/Regulatory Information
        programArea: metadata?.programArea ?? "",
        regulatoryProgram: metadata?.regulatoryProgram ?? "",
        regulatoryCitations: metadata?.regulatoryCitations ?? "",
        regulatoryAuthority: metadata?.regulatoryAuthority ?? "",

        // Important Dates
        issueDate: metadata?.issueDate ?? null,
        expirationDate: metadata?.expirationDate ?? null,
        effectiveDate: metadata?.effectiveDate ?? null,
        submissionDeadline: metadata?.submissionDeadline ?? null,
        dueDate: metadata?.dueDate ?? null,

        // Financial Information
        amount: metadata?.amount ?? null,
        currency: metadata?.currency ?? "",

        // Related Documents
        relatedDocumentIds: metadata?.relatedDocumentIds ?? [],
        supersedes: metadata?.supersedes ?? "",

        // Summary and Search
        summary: metadata?.summary ?? "",
        keywords: metadata?.keywords ?? [],
      });
    }
  }, [document, metadata, reset]);

  const handleSaveAndNext = useCallback(
    async (data: DocumentMetadataFormData) => {
      try {
        if (!currentDocumentId) {
          alerts.error("Document not selected");
          return;
        }

        // Only save if the form has been modified or the document needs review
        if (isDirty || metadata?.needsReview || !metadata) {
          if (!document) {
            alerts.error("Document not loaded");
            return;
          }

          // Build associations for facilities
          const associations: DocumentAssociationsInput[] = (
            data.facilities ?? []
          ).map((f) => ({
            tenantId: tenantId ?? "",
            facilityId: f.id,
          }));

          // Determine facilities to remove
          const initialFacilityIds =
            document.facilities?.map((f) => f.id) ?? [];
          const newFacilityIds = data.facilities?.map((f) => f.id) ?? [];
          const facilitiesToRemove = initialFacilityIds.filter(
            (id) => !newFacilityIds.includes(id)
          );
          const removeAssociations: DocumentAssociationsInput[] =
            facilitiesToRemove.map((id) => ({
              tenantId: tenantId ?? "",
              facilityId: id,
            }));

          await upsertDocumentWithMetadata({
            variables: {
              id: currentDocumentId,
              document: {
                id: document.id,
                tenantId: document.tenantId,
                title: data.documentTitle,
                documentType: data.documentType,
                authoredAt: data.dateAuthored,
                description: document.description,
                fileExtension: document.fileExtension,
                storageLink: document.storageLink,
                isArchived: false,
                documentTags: data.documentTags?.map((t) => ({
                  id: t.id,
                  name: t.name,
                })),
              },
              associations,
              removeAssociations,
              metadata: {
                documentType: data.documentType,
                documentSubtype: data.documentSubtype || null,
                documentNumber: data.documentNumber || null,
                relatedDocumentNumber: data.relatedDocumentNumber || null,
                revisionNumber: data.revisionNumber || null,
                reportingPeriod: data.reportingPeriod || null,
                siteName: data.siteName || null,
                siteAddressLine1: data.siteAddressLine1 || null,
                siteAddressLine2: data.siteAddressLine2 || null,
                siteCity: data.siteCity || null,
                siteState: data.siteState || null,
                siteZipCode: data.siteZipCode || null,
                siteEmail: data.siteEmail || null,
                mailingName: data.mailingName || null,
                mailingAddressLine1: data.mailingAddressLine1 || null,
                mailingAddressLine2: data.mailingAddressLine2 || null,
                mailingCity: data.mailingCity || null,
                mailingState: data.mailingState || null,
                mailingZipCode: data.mailingZipCode || null,
                mailingEmail: data.mailingEmail || null,
                contactName: data.contactName || null,
                contactEmail: data.contactEmail || null,
                contactPhone: data.contactPhone || null,
                contactAddressLine1: data.contactAddressLine1 || null,
                contactAddressLine2: data.contactAddressLine2 || null,
                contactCity: data.contactCity || null,
                contactState: data.contactState || null,
                contactZipCode: data.contactZipCode || null,
                agency: data.agency || null,
                programArea: data.programArea || null,
                regulatoryProgram: data.regulatoryProgram || null,
                regulatoryCitations: data.regulatoryCitations || null,
                regulatoryAuthority: data.regulatoryAuthority || null,
                issueDate: data.issueDate,
                expirationDate: data.expirationDate,
                effectiveDate: data.effectiveDate,
                submissionDeadline: data.submissionDeadline,
                dueDate: data.dueDate,
                amount: data.amount,
                currency: data.currency || null,
                lineItems: null,
                relatedDocumentIds: data.relatedDocumentIds ?? [],
                supersedes: data.supersedes || null,
                summary: data.summary || null,
                keywords: data.keywords ?? [],
              },
            },
          });

          alerts.success("Document and metadata saved successfully");
        }

        // Move to next document or close
        if (currentIndex < documentIds.length - 1) {
          const nextDocumentId = documentIds[currentIndex + 1];
          setCurrentDocumentId(nextDocumentId);
          onDocumentChange(nextDocumentId);
        } else {
          if (onComplete) {
            onComplete();
            onClose();
          }
        }
      } catch (error) {
        alerts.error("Failed to save document and metadata", error);
      }
    },
    [
      currentDocumentId,
      currentIndex,
      documentIds,
      upsertDocumentWithMetadata,
      alerts,
      onComplete,
      onClose,
      metadata,
      isDirty,
      document,
      tenantId,
      onDocumentChange,
      setCurrentDocumentId,
    ]
  );

  const handleSkipWithoutReviewing = useCallback(() => {
    // Move to next document without saving
    if (currentIndex < documentIds.length - 1) {
      const nextDocumentId = documentIds[currentIndex + 1];
      setCurrentDocumentId(nextDocumentId);
      onDocumentChange(nextDocumentId);
    }
  }, [currentIndex, documentIds, onDocumentChange]);

  const documentTypeOptions = useMemo(
    () =>
      Object.values(DocumentType).map((type) => ({
        value: type,
        display: documentTypeToLabel(type),
      })),
    []
  );

  // Compute dialog title based on context
  const dialogTitle = useMemo(() => {
    if (title) return title;

    if (hasDocumentAI && metadata?.needsReview) {
      return "Review & Confirm Details";
    }
    // When AI is not enabled
    return "Edit Document";
  }, [title, hasDocumentAI, metadata?.needsReview]);

  if (!open) return null;

  return (
    <FormProvider {...methods}>
      <DocumentDialogContainer
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={"lg"}
        title={dialogTitle}
        sx={{
          "& .MuiDialog-paper": {
            height: "960px", // fixed height so the dialog can scroll internally
          },
        }}
        contentSx={{ pt: 0 }}
        formContainerProps={{
          ref: scrollContainerRef,
        }}
        previewUrl={previewUrl}
        fileExtension={document?.fileExtension ?? undefined}
        previewLoading={loading}
        showPreviewBoxIfFailed={true}
        formColumnSize={{
          xs: 6,
        }}
        previewColumnSize={{
          xs: 6,
        }}
        secondaryActionElements={[
          <ButtonWithSkeleton
            key="previous"
            loading={loading}
            onClick={onClose}
            size="medium"
            variant="text"
            sx={{ color: theme.palette.text.secondary }}
          >
            {document?.documentMetadata?.needsReview
              ? "Close Without Reviewing"
              : "Close"}
          </ButtonWithSkeleton>,
        ]}
        additionalPrimaryActionElements={
          documentIds.length > 1 && currentIndex < documentIds.length - 1
            ? [
                <ButtonWithSkeleton
                  key="skip-without-reviewing"
                  loading={loading}
                  onClick={handleSkipWithoutReviewing}
                  disabled={currentIndex === documentIds.length - 1 || loading}
                  size="medium"
                  variant="text"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Skip
                </ButtonWithSkeleton>,
              ]
            : []
        }
        primaryActionButton={
          <ButtonWithSkeleton
            key="primary-action-button"
            loading={loading || saving}
            disabled={loading || saving}
            type="submit"
            variant="contained"
            onClick={handleSubmit(handleSaveAndNext)}
            endIcon={
              currentIndex < documentIds.length - 1 ? (
                <ChevronRight />
              ) : undefined
            }
          >
            {currentIndex < documentIds.length - 1
              ? "Save & Next Document"
              : `Save`}
          </ButtonWithSkeleton>
        }
        noPrimaryAction={false}
        hideCancelButton={true}
        titleBarActionElement={
          documentIds.length > 1 ? (
            <DocumentSwitcherSelect
              documentIds={documentIds}
              tenantId={tenantId}
              selectedId={currentDocumentId}
              skip={!open}
              disabled={documentIds.length <= 1}
              onSelect={handleSelectDocument}
            />
          ) : null
        }
      >
        <>
          {loading ? (
            <DocumentMetadataFormSectionsSkeleton />
          ) : (
            <DocumentMetadataFormSections
              control={control}
              document={document}
              suggestedFacilities={suggestedFacilities}
              suggestedFacilitiesLoading={suggestedFacilitiesLoading}
              documentTypeOptions={documentTypeOptions}
              tenantId={tenantId}
            />
          )}
        </>
      </DocumentDialogContainer>
    </FormProvider>
  );
}

/**
 *  A simple wrapper around the Button component that shows a skeleton when the loading prop is true.
 */
const ButtonWithSkeleton = <T extends ButtonProps>(
  props: T & { loading: boolean }
) => {
  const { loading, key, ...rest } = props;
  if (loading) {
    return <Skeleton key={key} width={100} height={32} />;
  }
  return <Button {...rest} />;
};
